// ============================================================================
// UNIFIED SIGN OUT UTILITY
// ============================================================================
//
// PURPOSE: Centralized sign out logic to prevent future breakage and reduce complexity
//
// FEATURES:
// - Single source of truth for all sign out operations
// - Automatic navigation to home page
// - Error handling and logging
// - Extensible for future cleanup operations
// - Comprehensive cookie clearing (Supabase and all auth cookies)
// - Browser storage clearing (localStorage, sessionStorage)
// - Server-side signout support
//
// USAGE:
// - Import and use directly: await signOut()
// - Use with custom redirect: await signOut({ redirectTo: '/custom-path' })
// - Use without navigation: await signOut({ redirect: false })
// - Clear all auth cookies: await signOut({ clearAllAuthCookies: true })
// - Complete cleanup: await signOutWithCompleteCleanup()
// - Server-side signout: await signOut({ serverSide: true })
//
// ============================================================================

import {
  DEFAULT_SIGN_OUT_SCOPE,
  getBrowserSupabaseClient,
  scopeEndsCurrentSession,
  toGoTrueSignOutScope,
  type SignOutScope,
} from "@west-tennessee-consulting/supabase";
import { navigateTo } from "./navigate.js";

// ============================================================================
// TYPES
// ============================================================================

export interface SignOutOptions {
  redirectTo?: string;
  redirect?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearCookies?: boolean;
  clearAllAuthCookies?: boolean;
  serverSide?: boolean;
  /**
   * How far the sign-out reaches. Defaults to `"device"` (auth-js `local`) —
   * this browser only. auth-js itself defaults to GLOBAL, which would end the
   * user's sessions on every device and every OAuth/MCP client; we never want
   * that implicitly. See the SignOutScope contract in `@west-tennessee-consulting/supabase`.
   */
  scope?: SignOutScope;
}

export interface SignOutResult {
  success: boolean;
  error?: string;
  redirected?: boolean;
}

// ============================================================================
// CORE SIGN OUT LOGIC
// ============================================================================

/**
 * Alternative signout that just clears cookies without calling Supabase API
 */
async function performCookieOnlySignOut(): Promise<{ success: boolean; error?: string }> {
  try {
    console.log("performCookieOnlySignOut: Clearing cookies without Supabase API call");
    
    // Clear all Supabase cookies directly
    const cookies = document.cookie.split(";");
    const supabaseCookies = [
      'sb-access-token',
      'sb-refresh-token',
      'supabase-auth-token',
      'supabase.auth.token',
      'supabase.auth.refresh_token',
      'supabase.auth.access_token',
      'supabase.auth.id_token',
      'supabase.auth.session',
      'supabase.auth.user',
      'supabase.auth.provider_token',
      'supabase.auth.provider_refresh_token'
    ];

    // Clear all cookies that contain supabase or auth
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name.includes("supabase") || name.includes("auth") || name.includes("session")) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        console.log(`Cleared cookie: ${name}`);
      }
    });

    console.log("Cookie-only signout successful");
    
return { success: true };
  } catch (error) {
    console.error("Error in cookie-only signout:", error);
    
return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
}

/**
 * Core sign out function that handles Supabase authentication cleanup
 * Uses direct createBrowserClient to bypass auth guard
 */
async function performSupabaseSignOut(
  scope: SignOutScope = DEFAULT_SIGN_OUT_SCOPE
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log("performSupabaseSignOut: Creating Supabase client (bypassing auth guard)");
    console.log("performSupabaseSignOut: Supabase URL:", process.env.NEXT_PUBLIC_SUPABASE_URL);
    console.log("performSupabaseSignOut: Supabase Anon Key exists:", !!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY);
    
    // Use getBrowserSupabaseClient to bypass the global auth guard
    const supabase = getBrowserSupabaseClient();
    
    console.log("performSupabaseSignOut: Supabase client created successfully");
    
    console.log("performSupabaseSignOut: Calling supabase.auth.signOut()");
    
    try {
      // Add timeout to prevent hanging
      const signOutPromise = supabase.auth.signOut({ scope: toGoTrueSignOutScope(scope) });
      const timeoutPromise = new Promise((_resolve, reject) => 
        setTimeout(() => reject(new Error("Signout timeout after 5 seconds")), 5000)
      );
      
      const result = await Promise.race([signOutPromise, timeoutPromise]) as any;
      console.log("performSupabaseSignOut: Signout result:", result);
      
      if (result?.error) {
        console.error("Supabase sign out error:", result.error);
        
return { success: false, error: result.error.message };
      }
      
      console.log("Supabase sign out successful");
      
return { success: true };
    } catch (timeoutError) {
      console.warn("Supabase signout timed out, proceeding with cookie clearing:", timeoutError);
      // Even if signout times out, we can still clear cookies and proceed
      
      return { success: true };
    }
  } catch (error) {
    console.error("Unexpected error during Supabase sign out:", error);
    
return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
}

/**
 * Clear browser storage based on options
 */
function clearBrowserStorage(options: SignOutOptions): void {
  if (options.clearLocalStorage) {
    try {
      localStorage.clear();
      console.log("Local storage cleared");
    } catch (error) {
      console.error("Error clearing local storage:", error);
    }
  }
  
  if (options.clearSessionStorage) {
    try {
      sessionStorage.clear();
      console.log("Session storage cleared");
    } catch (error) {
      console.error("Error clearing session storage:", error);
    }
  }
}

/**
 * Clear a specific cookie with proper domain and path handling
 */
function clearCookie(name: string, domain?: string, path: string = "/"): void {
  try {
    // Clear for current domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    
    // Clear for parent domain if we're on a subdomain
    if (domain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
    }
    
    // Clear for root domain
    const hostname = window.location.hostname;
    if (hostname.includes('.')) {
      const rootDomain = hostname.split('.').slice(-2).join('.');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=.${rootDomain}`;
    }
    
    // Clear without domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
  } catch (error) {
    console.error(`Error clearing cookie ${name}:`, error);
  }
}

/**
 * Clear cookies based on options with comprehensive Supabase cookie handling
 */
function clearBrowserCookies(options: SignOutOptions): void {
  if (options.clearAllAuthCookies) {
    try {
      // Get all cookies
      const cookies = document.cookie.split(";");
      const cookieNames = cookies.map(cookie => {
        const eqPos = cookie.indexOf("=");
        
return eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      });

      // Clear all auth-related cookies
      const authCookiePatterns = [
        /auth/i,
        /supabase/i,
        /session/i,
        /token/i,
        /jwt/i,
        /oauth/i,
        /login/i,
        /user/i,
        /access/i,
        /refresh/i,
        /id_token/i,
        /access_token/i,
        /refresh_token/i
      ];

      cookieNames.forEach(name => {
        if (authCookiePatterns.some(pattern => pattern.test(name))) {
          clearCookie(name);
          console.log(`Cleared auth cookie: ${name}`);
        }
      });

      // Specifically clear known Supabase cookies
      const knownSupabaseCookies = [
        'sb-access-token',
        'sb-refresh-token',
        'supabase-auth-token',
        'supabase.auth.token',
        'supabase.auth.refresh_token',
        'supabase.auth.access_token',
        'supabase.auth.id_token',
        'supabase.auth.session',
        'supabase.auth.user',
        'supabase.auth.provider_token',
        'supabase.auth.provider_refresh_token'
      ];

      knownSupabaseCookies.forEach(cookieName => {
        clearCookie(cookieName);
        console.log(`Cleared known Supabase cookie: ${cookieName}`);
      });

      console.log("All auth cookies cleared comprehensively");
    } catch (error) {
      console.error("Error clearing all auth cookies:", error);
    }
  } else if (options.clearCookies) {
    try {
      // Clear Supabase-specific cookies with comprehensive patterns
      const cookies = document.cookie.split(";");
      const cookieNames = cookies.map(cookie => {
        const eqPos = cookie.indexOf("=");
        
return eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      });

      // Clear cookies that match Supabase patterns
      const supabasePatterns = [
        /^sb-/i,
        /supabase/i,
        /supabase\.auth/i
      ];

      cookieNames.forEach(name => {
        if (supabasePatterns.some(pattern => pattern.test(name))) {
          clearCookie(name);
          console.log(`Cleared Supabase cookie: ${name}`);
        }
      });

      // Specifically clear known Supabase cookies
      const knownSupabaseCookies = [
        'sb-access-token',
        'sb-refresh-token',
        'supabase-auth-token',
        'supabase.auth.token',
        'supabase.auth.refresh_token',
        'supabase.auth.access_token',
        'supabase.auth.id_token',
        'supabase.auth.session',
        'supabase.auth.user',
        'supabase.auth.provider_token',
        'supabase.auth.provider_refresh_token'
      ];

      knownSupabaseCookies.forEach(cookieName => {
        clearCookie(cookieName);
        console.log(`Cleared known Supabase cookie: ${cookieName}`);
      });

      console.log("Supabase cookies cleared comprehensively");
    } catch (error) {
      console.error("Error clearing Supabase cookies:", error);
    }
  }
}

/**
 * Navigate to specified URL or home page
 */
function navigateAfterSignOut(redirectTo?: string): void {
  try {
    const targetUrl = redirectTo || "/";
    
    // Use window.location with delay for Chrome compatibility
    setTimeout(() => {
      navigateTo(targetUrl);
      console.log(`Navigated to: ${targetUrl}`);
    }, 100);
  } catch (error) {
    console.error("Error during navigation:", error);
    // Fallback to home page with Chrome-compatible delay
    setTimeout(() => {
      navigateTo("/");
    }, 100);
  }
}

// ============================================================================
// MAIN SIGN OUT FUNCTION
// ============================================================================

/**
 * Unified sign out function that handles all cleanup and navigation
 * 
 * @param options - Configuration options for sign out behavior
 * @returns Promise<SignOutResult> - Result of the sign out operation
 */
/**
 * End every OTHER session for this user, keeping the current one. Powers the
 * account UI's "Sign out my other devices"; never wired to a Sign out button.
 */
export function signOutOtherDevices(): Promise<SignOutResult> {
  return signOut({ scope: "others", redirect: false });
}

/**
 * End EVERY session including this one and revoke all refresh tokens. Also
 * disconnects MCP/OAuth clients on other machines — by design. Explicit user
 * choice only.
 */
export function signOutEverywhere(
  options: Omit<SignOutOptions, "scope"> = {}
): Promise<SignOutResult> {
  return signOut({ ...options, scope: "everywhere" });
}

export async function signOut(options: SignOutOptions = {}): Promise<SignOutResult> {
  const {
    redirectTo = "/",
    clearAllAuthCookies = false,
    serverSide = false,
    scope = DEFAULT_SIGN_OUT_SCOPE,
  } = options;

  // `scope: "others"` keeps THIS session alive, so it must never clear this
  // browser's storage/cookies or navigate away — regardless of the other flags.
  const endsCurrent = scopeEndsCurrentSession(scope);
  const redirect = endsCurrent ? (options.redirect ?? true) : false;
  const clearLocalStorage = endsCurrent ? (options.clearLocalStorage ?? true) : false;
  const clearSessionStorage = endsCurrent ? (options.clearSessionStorage ?? true) : false;
  const clearCookies = endsCurrent ? (options.clearCookies ?? true) : false;

  console.log("signOut: Starting unified sign out process", { options, scope });

  try {
    // Step 1: Try Supabase sign out first, fallback to cookie-only if it hangs
    console.log("signOut: Step 1 - Performing Supabase sign out");
    let supabaseResult;
    
    try {
      supabaseResult = await performSupabaseSignOut(scope);
      console.log("signOut: Supabase sign out result", supabaseResult);
    } catch (error) {
      console.warn("signOut: Supabase signout failed, falling back to cookie-only signout:", error);
      // The cookie-only fallback ends THIS browser's session locally. That is
      // the right rescue when the user asked to leave this device — but for
      // `scope: "others"` it would sign out the very session they kept, so the
      // failure is reported instead of "fixed" by logging them out here.
      if (endsCurrent) {
        supabaseResult = await performCookieOnlySignOut();
        console.log("signOut: Cookie-only sign out result", supabaseResult);
      } else {
        supabaseResult = {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    }
    
    // Step 2: Clear browser storage (only if not server-side)
    if (!serverSide) {
      console.log("signOut: Step 2 - Clearing browser storage");
      clearBrowserStorage({ clearLocalStorage, clearSessionStorage });
      
      // Step 3: Clear cookies (only if not server-side)
      console.log("signOut: Step 3 - Clearing cookies");
      clearBrowserCookies({
        clearCookies,
        clearAllAuthCookies: endsCurrent && clearAllAuthCookies,
      });
    }
    
    // Step 4: Navigate if requested (only if not server-side)
    if (redirect && !serverSide) {
      console.log("signOut: Step 4 - Navigating to", redirectTo);
      navigateAfterSignOut(redirectTo);
    }

    const result: SignOutResult = {
      success: supabaseResult.success,
      error: supabaseResult.error,
      redirected: redirect && !serverSide,
    };

    console.log("signOut: Sign out process completed", result);
    
return result;

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error("Error in unified sign out process:", error);
    
    // Even if there's an error, try to navigate to ensure user gets redirected (only if not server-side)
    if (redirect && !serverSide) {
      navigateAfterSignOut(redirectTo);
    }

    return {
      success: false,
      error: errorMessage,
      redirected: redirect && !serverSide,
    };
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Sign out and redirect to home page (default behavior)
 */
export async function signOutToHome(): Promise<SignOutResult> {
  return signOut({ redirectTo: "/" });
}

/**
 * Sign out and redirect to login page
 */
export async function signOutToLogin(): Promise<SignOutResult> {
  return signOut({ redirectTo: "/login" });
}

/**
 * Sign out without navigation (useful for programmatic cleanup)
 */
export async function signOutSilent(): Promise<SignOutResult> {
  return signOut({ redirect: false });
}

/**
 * Sign out with full cleanup (storage + cookies + navigation)
 */
export async function signOutWithCleanup(): Promise<SignOutResult> {
  return signOut({
    redirect: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearCookies: true,
  });
}

/**
 * Sign out with complete cleanup (all auth cookies + storage + navigation)
 */
export async function signOutWithCompleteCleanup(): Promise<SignOutResult> {
  return signOut({
    redirect: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearAllAuthCookies: true,
  });
}

/**
 * Sign out with comprehensive Supabase cookie clearing
 */
export async function signOutWithSupabaseCleanup(): Promise<SignOutResult> {
  return signOut({
    redirect: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearCookies: true,
    clearAllAuthCookies: true,
  });
}

/**
 * Sign out using server-side API for comprehensive cookie clearing
 */
export async function signOutServerSide(): Promise<SignOutResult> {
  try {
    // Call server-side signout API
    const response = await fetch('/api/auth/signout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      console.error("Server-side signout failed:", result.error);
      
return {
        success: false,
        error: result.error || "Server-side signout failed",
        redirected: false,
      };
    }

    // Clear client-side storage
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log("Client-side storage cleared after server signout");
    } catch (error) {
      console.error("Error clearing client-side storage:", error);
    }

    // Navigate to home page with Chrome-compatible delay
    setTimeout(() => {
      navigateTo("/");
    }, 100);

    return {
      success: true,
      redirected: true,
    };

  } catch (error) {
    console.error("Error in server-side signout:", error);
    
return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      redirected: false,
    };
  }
}

// ============================================================================
// NOTE: React hook moved to hooks/use-sign-out.tsx to avoid client/server conflicts
// ============================================================================
